import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

// Layout
import StandardLayout from '../../components/layout/StandardLayout';

// Components
import QuestionCard from './QuestionCard';
import CategoryDropdown from './CategoryDropdown';

// Services
import { loadAllQuestionsAndAllCategories, loadCategories } from '../../services/questionAPIService';

// Icons
import { 
  BookOpenIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowsUpDownIcon,
  XMarkIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  BeakerIcon,
} from '@heroicons/react/24/outline';

const QuestionList = () => {
  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('default');
  const [filters, setFilters] = useState({
    onlyUnanswered: false,
    onlyIncorrect: false,
    onlyCorrect: false,
    selectedCatalog: null,
    selectedExamPart: null
  });

  const location = useLocation();

  // Extrahiere einzigartige Kataloge und Prüfungsteile
  const uniqueCatalogs = useMemo(() => {
    const catalogs = new Set();
    questions.forEach(question => {
      question.catalogs?.forEach(catalog => {
        catalogs.add(JSON.stringify(catalog));
      });
    });
    return Array.from(catalogs).map(c => JSON.parse(c));
  }, [questions]);

  const uniqueExamParts = useMemo(() => {
    const examParts = new Set();
    questions.forEach(question => {
      question.exam_subcategories?.forEach(part => {
        examParts.add(JSON.stringify(part));
      });
    });
    return Array.from(examParts).map(p => JSON.parse(p));
  }, [questions]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [questionsData, categoriesData] = await Promise.all([
          loadAllQuestionsAndAllCategories(),
          loadCategories()
        ]);

        // Verarbeite URL-Parameter
        const queryParams = new URLSearchParams(location.search);
        const catalogId = queryParams.get('catalog');
        const examPartId = queryParams.get('examPart');
        
        if (catalogId) {
          setFilters(prev => ({ ...prev, selectedCatalog: parseInt(catalogId) }));
        }
        if (examPartId) {
          setFilters(prev => ({ ...prev, selectedExamPart: parseInt(examPartId) }));
        }

        setQuestions(questionsData.map(question => ({
          ...question,
          category: question.category_id,
          category_name: question.category_name,
          catalogs: question.catalogs || [],
          exam_subcategories: question.exam_subcategories || [],
          images: {
            question: question.question_image || null,
            answer: question.answer_image || null,
            explanation: question.explanation_image || null
          },
          answer: question.answer_text,
          explanation: question.explanation_text,
          statistics: {
            correct_count: question.correct_count,
            incorrect_count: question.incorrect_count,
            streak: question.streak,
            last_answered: question.last_answered
          }
        })));

        const initialCategories = categoriesData.map(category => ({
          id: category.id,
          name: category.name,
          checked: true
        }));
        setCategories(initialCategories);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [location.search]);

  const filteredAndSortedQuestions = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    const categoryId = queryParams.get('category');
    const catalogId = queryParams.get('catalog');

    // First apply category and catalog filters
    let filtered = questions.filter(question =>
      categories.some(category => category.checked && category.id === question.category) &&
      (!categoryId || question.category === parseInt(categoryId)) &&
      (!filters.selectedCatalog || question.catalogs.some(cat => cat.id === filters.selectedCatalog)) &&
      (!filters.selectedExamPart || question.exam_subcategories.some(part => part.id === filters.selectedExamPart))
    );

    // Apply search term
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(question =>
        question.question_text.toLowerCase().includes(searchLower) ||
        question.answer.toLowerCase().includes(searchLower) ||
        question.category_name.toLowerCase().includes(searchLower)
      );
    }

    // Apply additional filters
    if (filters.onlyUnanswered) {
      filtered = filtered.filter(q => 
        !q.statistics.last_answered
      );
    }
    if (filters.onlyIncorrect) {
      filtered = filtered.filter(q => 
        q.statistics.incorrect_count > q.statistics.correct_count
      );
    }
    if (filters.onlyCorrect) {
      filtered = filtered.filter(q => 
        q.statistics.correct_count > 0 && q.statistics.correct_count > q.statistics.incorrect_count
      );
    }

    // Apply sorting
    switch (sortBy) {
      case 'lastAnswered':
        return filtered.sort((a, b) => {
          if (!a.statistics.last_answered) return 1;
          if (!b.statistics.last_answered) return -1;
          return new Date(b.statistics.last_answered) - new Date(a.statistics.last_answered);
        });
      case 'mostIncorrect':
        return filtered.sort((a, b) => 
          b.statistics.incorrect_count - a.statistics.incorrect_count
        );
      case 'successRate':
        return filtered.sort((a, b) => {
          const rateA = a.statistics.correct_count / (a.statistics.correct_count + a.statistics.incorrect_count) || 0;
          const rateB = b.statistics.correct_count / (b.statistics.correct_count + b.statistics.incorrect_count) || 0;
          return rateB - rateA;
        });
      default:
        return filtered;
    }
  }, [questions, categories, searchTerm, sortBy, filters, location.search]);

  if (loading) {
    return (
      <StandardLayout>
        <div className="min-h-screen flex items-center justify-center bg-base-100">
          <div className="flex flex-col items-center gap-4">
            <span className="loading loading-spinner loading-lg text-primary"></span>
            <p className="text-base-content/70 animate-pulse">Lade Fragen...</p>
          </div>
        </div>
      </StandardLayout>
    );
  }

  return (
    <StandardLayout>
      <div className="min-h-screen bg-base-100">
        {/* Hero Section */}
        <div>
          <div className="container mx-auto px-4 py-6 max-w-5xl">
            <div className="text-center">
              <div className="inline-flex p-4 rounded-full bg-primary/10 ring-1 ring-primary/20 mb-6">
                <BookOpenIcon className="w-8 h-8 text-primary" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold mb-3 text-base-content">
                Fragenkatalog
              </h1>
              <p className="text-base-content/70 max-w-2xl mx-auto">
                Hier findest du alle Prüfungsfragen zum SKS-Schein. Nutze die Filter, um gezielt zu lernen.
              </p>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="container mx-auto px-4 py-8 max-w-5xl">
          {/* Search and Filter Section */}
          <div className="card bg-base-200 shadow-lg ring-1 ring-base-content/5 mb-8">
            <div className="card-body p-4">
              {/* Search Bar */}
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-base-content/50" />
                  </div>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Suche nach Fragen..."
                    className="input input-bordered w-full pl-10"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <XMarkIcon className="h-5 w-5 text-base-content/50 hover:text-base-content" />
                    </button>
                  )}
                </div>
                <div className="flex gap-2">
                  <CategoryDropdown
                    categories={categories}
                    setCategories={setCategories}
                  />
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`btn btn-sm gap-2 ${showFilters ? 'btn-primary' : ''}`}
                  >
                    <FunnelIcon className="w-4 h-4" />
                    Filter
                  </button>
                  <div className="dropdown dropdown-end">
                    <label tabIndex={0} className="btn btn-sm gap-2">
                      <ArrowsUpDownIcon className="w-4 h-4" />
                      Sortieren
                    </label>
                    <ul tabIndex={0} className="dropdown-content z-[1] menu p-2 shadow-lg bg-base-100 rounded-lg w-52 mt-2">
                      <li>
                        <button
                          className={sortBy === 'default' ? 'active' : ''}
                          onClick={() => setSortBy('default')}
                        >
                          Standard
                        </button>
                      </li>
                      <li>
                        <button
                          className={sortBy === 'lastAnswered' ? 'active' : ''}
                          onClick={() => setSortBy('lastAnswered')}
                        >
                          Zuletzt beantwortet
                        </button>
                      </li>
                      <li>
                        <button
                          className={sortBy === 'mostIncorrect' ? 'active' : ''}
                          onClick={() => setSortBy('mostIncorrect')}
                        >
                          Häufigste Fehler
                        </button>
                      </li>
                      <li>
                        <button
                          className={sortBy === 'successRate' ? 'active' : ''}
                          onClick={() => setSortBy('successRate')}
                        >
                          Erfolgsquote
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Additional Filters */}
              {showFilters && (
                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    onClick={() => setFilters(f => ({ ...f, onlyUnanswered: !f.onlyUnanswered }))}
                    className={`btn btn-sm gap-2 ${filters.onlyUnanswered ? 'btn-primary' : 'btn-ghost'}`}
                  >
                    <ClockIcon className="w-4 h-4" />
                    Nur unbeantwortete
                  </button>
                  <button
                    onClick={() => setFilters(f => ({ ...f, onlyIncorrect: !f.onlyIncorrect }))}
                    className={`btn btn-sm gap-2 ${filters.onlyIncorrect ? 'btn-primary' : 'btn-ghost'}`}
                  >
                    <XCircleIcon className="w-4 h-4" />
                    Häufige Fehler
                  </button>
                  <button
                    onClick={() => setFilters(f => ({ ...f, onlyCorrect: !f.onlyCorrect }))}
                    className={`btn btn-sm gap-2 ${filters.onlyCorrect ? 'btn-primary' : 'btn-ghost'}`}
                  >
                    <CheckCircleIcon className="w-4 h-4" />
                    Erfolgreich gelernt
                  </button>

                  {/* Katalog Filter */}
                  <div className="dropdown dropdown-end">
                    <label tabIndex={0} className={`btn btn-sm gap-2 ${filters.selectedCatalog ? 'btn-primary' : 'btn-ghost'}`}>
                      <BookOpenIcon className="w-4 h-4" />
                      {filters.selectedCatalog 
                        ? uniqueCatalogs.find(c => c.id === filters.selectedCatalog)?.name 
                        : 'Katalog wählen'}
                    </label>
                    <ul tabIndex={0} className="dropdown-content z-[1] menu p-2 shadow-lg bg-base-100 rounded-lg w-52 mt-2">
                      {filters.selectedCatalog && (
                        <li>
                          <button onClick={() => setFilters(f => ({ ...f, selectedCatalog: null }))}>
                            Alle Kataloge
                          </button>
                        </li>
                      )}
                      {uniqueCatalogs.map((catalog) => (
                        <li key={catalog.id}>
                          <button
                            className={filters.selectedCatalog === catalog.id ? 'active' : ''}
                            onClick={() => setFilters(f => ({ ...f, selectedCatalog: catalog.id }))}
                          >
                            {catalog.name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Prüfungsteil Filter */}
                  <div className="dropdown dropdown-end">
                    <label tabIndex={0} className={`btn btn-sm gap-2 ${filters.selectedExamPart ? 'btn-primary' : 'btn-ghost'}`}>
                      <BeakerIcon className="w-4 h-4" />
                      {filters.selectedExamPart 
                        ? uniqueExamParts.find(p => p.id === filters.selectedExamPart)?.title 
                        : 'Prüfungsteil wählen'}
                    </label>
                    <ul tabIndex={0} className="dropdown-content z-[1] menu p-2 shadow-lg bg-base-100 rounded-lg w-52 mt-2">
                      {filters.selectedExamPart && (
                        <li>
                          <button onClick={() => setFilters(f => ({ ...f, selectedExamPart: null }))}>
                            Alle Prüfungsteile
                          </button>
                        </li>
                      )}
                      {uniqueExamParts.map((part) => (
                        <li key={part.id}>
                          <button
                            className={filters.selectedExamPart === part.id ? 'active' : ''}
                            onClick={() => setFilters(f => ({ ...f, selectedExamPart: part.id }))}
                          >
                            {part.title}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Results Count */}
              <div className="mt-4 text-sm text-base-content/70">
                {filteredAndSortedQuestions.length} Fragen gefunden
              </div>
            </div>
          </div>

          {/* Questions List */}
          <div className="space-y-4">
            {filteredAndSortedQuestions.length === 0 ? (
              <div className="card bg-base-100 shadow-lg ring-1 ring-base-content/5">
                <div className="card-body text-center py-12">
                  <h3 className="text-lg font-bold text-base-content/70">
                    Keine Fragen gefunden
                  </h3>
                  <p className="text-sm text-base-content/60">
                    Bitte passe deine Suchkriterien an.
                  </p>
                </div>
              </div>
            ) : (
              filteredAndSortedQuestions.map((question) => (
                question && (
                  <QuestionCard
                    key={question.id}
                    question={question}
                  />
                )
              ))
            )}
          </div>
        </div>
      </div>
    </StandardLayout>
  );
};

export default QuestionList;
